/**
 * What a point of interest is worth walking to right now. The score is four multipliers — how safe
 * the place is at this hour, how many of the travellers want it, how soon its window closes, and how
 * close it is — and the values are the spec's, not to be re-derived here. The heatmap and the ranked
 * list both read this one function, so the map and the list can never disagree about an order.
 *
 * The contract is `danger.ts`'s, carried across: nothing here throws, and a non-finite input (NaN or
 * either infinity) means "no information". A non-finite vote count is one vote, a non-finite clock
 * carries no urgency and moves no level, a non-finite coordinate is in no sector and a non-finite
 * walk takes no distance penalty. On top of that, no term is ever zero or negative: the ordinary
 * case is nothing urgent and nobody having voted, and a zero anywhere in the product would empty
 * the map exactly then.
 */

import { adjustedLevel, sectorAt, type Level, type Mode, type SectorSnapshot } from "./danger.ts";

/**
 * The safety multiplier for each hour-adjusted level. Never zero: danger is a weight, not a veto,
 * and Bellas Artes sits at level 6 and must still rank.
 */
export const SAFETY_BY_LEVEL: Readonly<Record<Level, number>> = {
  1: 1.0,
  2: 0.85,
  3: 0.68,
  4: 0.48,
  5: 0.3,
  6: 0.15,
};

/** Walking pace, in metres per second, over straight-line distance. No routing, no network. */
export const WALK_PACE_MPS = 1.4;
/** The walk, in minutes, at which closeness halves a score. */
export const PROXIMITY_HALF_MINUTES = 15;
/** A window that is closing right now multiplies its score by `1 + URGENCY_NUMERATOR`. */
export const URGENCY_NUMERATOR = 3;
/**
 * How quickly the run-up to a window fades with distance in time. A window opening later today
 * lifts its score by half of what it would be worth open at the moment it opens, a third of it
 * this many hours before, a quarter at twice this, and so on. Two hours is the span in which "be
 * nearby when it opens" is something you can still act on by walking; further out the lift is
 * kept, so the run-up never steps, but it is small enough not to send anybody early.
 */
export const UPCOMING_LIFT_HOURS = 2;

/** Mexico City's zone, where every window's days and hours are read regardless of the device's. */
const ZONE = "America/Mexico_City";
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** Mean Earth radius, in metres; the spherical error is far below the error of walking straight. */
const EARTH_R_M = 6_371_000;

/**
 * When a POI is worth visiting, as somebody typed it. `days` counts from 0 = Sunday, as JavaScript
 * and Postgres's `dow` both do; `null` means any day. The hours are a pair or both `null` for "any
 * hour"; an end at or before the start runs past midnight into the following day, so 22 to 2 is a
 * four-hour window. A field that cannot be read — a non-finite or out-of-range hour, a day outside
 * 0 to 6 — carries no information and is read as blank.
 */
export type PoiWindow = { days: number[] | null; startHour: number | null; endHour: number | null };

/**
 * Where now stands against a POI's windows. `OPEN` has `hoursLeft` until it closes. `UPCOMING` opens
 * later today: `hoursUntil` until it does, and `hoursLeft` how long it will then stay open.
 * `PASSED` has nothing left today but recurs, `hoursUntil` away. `ALWAYS` has nothing to close.
 * Every number is finite and at least zero; a field with no meaning for the kind is `null`.
 */
export type WindowState = {
  kind: "ALWAYS" | "OPEN" | "UPCOMING" | "PASSED";
  hoursLeft: number | null;
  hoursUntil: number | null;
};

/**
 * Whether a clock reading can be placed on a calendar at all. Past 8.64e15 ms either way a `Date` is
 * invalid and `Intl` throws, so a reading out there carries no more information than NaN does.
 */
const isTime = (ms: number) => Number.isFinite(ms) && Math.abs(ms) <= 8.64e15;

const ALWAYS: WindowState = { kind: "ALWAYS", hoursLeft: null, hoursUntil: null };

let formatter: Intl.DateTimeFormat | undefined;

/**
 * The local midnight that starts the day `nowMs` falls in, that day's weekday, and the local hour.
 * The day after is taken to be exactly 24 hours later, true in Mexico City, which has kept no
 * daylight saving since 2022; a zone that does would be an hour out on its changeover days.
 */
function localDay(nowMs: number): { midnight: number; weekday: number; hour: number } {
  formatter ??= new Intl.DateTimeFormat("en-US", {
    timeZone: ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
  const p: Record<string, number> = {};
  for (const { type, value } of formatter.formatToParts(nowMs)) p[type] = Number(value);
  const sinceMidnight =
    ((p.hour! * 60 + p.minute!) * 60 + p.second!) * 1000 + (((nowMs % 1000) + 1000) % 1000);
  return {
    midnight: nowMs - sinceMidnight,
    weekday: new Date(Date.UTC(p.year!, p.month! - 1, p.day!)).getUTCDay(),
    hour: sinceMidnight / HOUR_MS,
  };
}

/** An hour that cannot be read is blank; the start may be 0 to 24 exclusive, the end 0 exclusive to 24. */
function readHours(w: PoiWindow): [number, number] | null {
  const { startHour: a, endHour: b } = w;
  if (a === null || b === null || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a < 0 || a >= 24 || b <= 0 || b > 24) return null;
  return [a, b];
}

/** Days outside 0 to 6 are dropped; if none are left, or there were none, any day will do. */
function readDays(w: PoiWindow): Set<number> | null {
  if (!Array.isArray(w.days)) return null;
  const days = new Set(w.days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6));
  return days.size === 0 ? null : days;
}

/**
 * Where now stands against a POI's windows. Each window becomes its concrete stretches of time from
 * yesterday to a week ahead — yesterday, so that last night's 22 to 2 is still open at 1am; a week
 * ahead, so that a window on today's weekday that has already closed finds its next occurrence.
 * Stretches that overlap or touch are merged, across windows as well as within one, so lunch from
 * 13 to 16 and a sitting from 15 to 18 close at 18, and a whole Friday followed by a whole Saturday
 * closes on Saturday night.
 *
 * Windows are half-open: at the opening instant the full window is left, and at the closing
 * instant it is shut, never open with nothing to go. Open beats upcoming, which beats having passed.
 * A window that is blank, or covers every day at every hour, can never close, so the POI is `ALWAYS`;
 * so is a POI with no windows, and one asked about at a non-finite time, which carries no urgency.
 */
export function windowStateAt(nowMs: number, windows: PoiWindow[]): WindowState {
  if (!isTime(nowMs) || windows.length === 0) return ALWAYS;
  const { midnight, weekday } = localDay(nowMs);

  const spans: [number, number][] = [];
  for (const w of windows) {
    const hours = readHours(w);
    const days = readDays(w);
    const [from, to] = hours ?? [0, 24];
    const length = to > from ? to - from : to + 24 - from;
    if (length >= 24 && (days === null || days.size === 7)) return ALWAYS;
    for (let k = -1; k <= 7; k++) {
      if (days !== null && !days.has((((weekday + k) % 7) + 7) % 7)) continue;
      const start = midnight + k * DAY_MS + from * HOUR_MS;
      spans.push([start, start + length * HOUR_MS]);
    }
  }

  spans.sort((x, y) => x[0] - y[0]);
  const merged: [number, number][] = [];
  for (const s of spans) {
    const last = merged[merged.length - 1];
    if (last && s[0] <= last[1]) last[1] = Math.max(last[1], s[1]);
    else merged.push([s[0], s[1]]);
  }

  const open = merged.find(([a, b]) => a <= nowMs && nowMs < b);
  if (open) return { kind: "OPEN", hoursLeft: (open[1] - nowMs) / HOUR_MS, hoursUntil: null };
  const next = merged.find(([a]) => a > nowMs);
  if (!next) return { kind: "PASSED", hoursLeft: null, hoursUntil: null }; // unreachable: every window recurs within the week
  const hoursUntil = (next[0] - nowMs) / HOUR_MS;
  return next[0] < midnight + DAY_MS
    ? { kind: "UPCOMING", hoursLeft: (next[1] - next[0]) / HOUR_MS, hoursUntil }
    : { kind: "PASSED", hoursLeft: null, hoursUntil };
}

/**
 * Metres between two coordinates, in a straight line over a sphere. Any non-finite coordinate
 * gives NaN, which is how this module says "unknown" — never a distance of nought.
 */
export function metresBetween(aLat: number, aLon: number, bLat: number, bLon: number): number {
  if (![aLat, aLon, bLat, bLon].every(Number.isFinite)) return NaN;
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLon = (bLon - aLon) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Minutes on foot, in a straight line at `WALK_PACE_MPS`, by the haversine distance. Any non-finite
 * coordinate gives NaN, which is how this module says "unknown"; `proximityTerm` reads it as such.
 */
export function walkMinutes(aLat: number, aLon: number, bLat: number, bLon: number): number {
  return metresBetween(aLat, aLon, bLat, bLon) / WALK_PACE_MPS / 60;
}

/**
 * The safety multiplier for an hour-adjusted level. `null` is a POI in no sector: no penalty, and
 * the caller says "no data". A value that is not a level fails cautious, at level 6, as `levelOf`
 * does with a score it cannot place.
 */
export function safetyTerm(level: Level | null): number {
  if (level === null) return 1;
  return SAFETY_BY_LEVEL[level] ?? SAFETY_BY_LEVEL[6];
}

/**
 * 1.0 for none or one vote, 1.5 for two, 2.0 for three, held at 2.0 beyond. Nobody voting counts as
 * one person wanting it: importing a saved list is itself an expression of wanting the places on it.
 */
export function wantedTerm(votes: number): number {
  if (!Number.isFinite(votes)) return 1;
  return 1 + (Math.min(3, Math.max(1, Math.floor(votes))) - 1) / 2;
}

/** The open urgency, `1 + 3 / (1 + hoursLeft)`, with hours-left that cannot be read taken as none urgent. */
function openUrgency(hoursLeft: number | null): number {
  if (hoursLeft === null || !Number.isFinite(hoursLeft)) return 1;
  return 1 + URGENCY_NUMERATOR / (1 + Math.max(0, hoursLeft));
}

/**
 * How urgent the window makes a POI. Open, it is `1 + 3 / (1 + hoursLeft)`: 4.0 as it closes,
 * falling toward 1.0. Upcoming, it is a part of the open lift it will have once it opens — a half at
 * the opening instant, shrinking with `hoursUntil` on the `UPCOMING_LIFT_HOURS` scale — so it is
 * always above the floor and always strictly below the open urgency for the same hours, and never
 * flares. Passed and always-open sit on the floor, 1.0. A number that cannot be read lifts nothing.
 */
export function urgencyTerm(state: WindowState): number {
  if (state.kind === "OPEN") return openUrgency(state.hoursLeft);
  if (state.kind !== "UPCOMING") return 1;
  const until = state.hoursUntil;
  if (until === null || !Number.isFinite(until)) return 1;
  const lift = openUrgency(state.hoursLeft) - 1;
  return 1 + lift / (2 + Math.max(0, until) / UPCOMING_LIFT_HOURS);
}

/**
 * `1 / (1 + walkMinutes / 15)`: 1.0 on the spot, a half at fifteen minutes, never zero. A walk
 * that is not finite is unknown, most often because the traveller has no fix yet, and takes no
 * penalty, so every POI is treated alike until there is one. A negative walk is no walk.
 */
export function proximityTerm(walkMinutes: number): number {
  if (!Number.isFinite(walkMinutes)) return 1;
  return 1 / (1 + Math.max(0, walkMinutes) / PROXIMITY_HALF_MINUTES);
}

export type PoiScoreInput = {
  lat: number;
  lon: number;
  fromLat: number;
  fromLon: number;
  votes: number;
  windows: PoiWindow[];
  nowMs: number;
  mode: Mode;
  sectors: SectorSnapshot[];
};

/**
 * What a POI is worth: safety × wanted × urgency × proximity, each term reported so the interface
 * can say why. `level` is the hour-adjusted level of the POI's sector in Mexico City's local hour,
 * on the mode's own score, or `null` outside every sector.
 */
export function poiScore(input: PoiScoreInput): {
  score: number;
  safety: number;
  wanted: number;
  urgency: number;
  proximity: number;
  level: Level | null;
} {
  const { lat, lon, fromLat, fromLon, votes, windows, nowMs, mode, sectors } = input;
  const sector = sectorAt(lat, lon, sectors);
  const hour = isTime(nowMs) ? localDay(nowMs).hour : NaN;
  const level =
    sector === null
      ? null
      : adjustedLevel(mode === "FOOT" ? sector.foot.score : sector.vehicle.score, hour, mode);
  const safety = safetyTerm(level);
  const wanted = wantedTerm(votes);
  const urgency = urgencyTerm(windowStateAt(nowMs, windows));
  const proximity = proximityTerm(walkMinutes(fromLat, fromLon, lat, lon));
  return {
    score: safety * wanted * urgency * proximity,
    safety,
    wanted,
    urgency,
    proximity,
    level,
  };
}

/**
 * A place name as every comparison of names reads it: lower case, accents gone, spaces collapsed,
 * so "Taquería  EL" and "taqueria el" are one name. The API's duplicate check on a Google list sync
 * and both search fields (the map's and the Places page's) use this one rule, so they cannot
 * disagree about whether two names are the same or whether a typed word is in a name.
 */
export const foldName = (s: string) =>
  s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();
